/**
 * CollisionService
 * Main orchestrator for collision detection between aircraft and hangar walls
 * Implements two-stage detection: OBB broad phase → BVH narrow phase
 */

import * as THREE from 'three'
import { OBB } from 'three/examples/jsm/math/OBB.js'
import {
  createConvexHullOBB,
  createMultiComponentOBBs,
  ensureBVHForObject,
  shouldUseMultiComponent,
} from './CollisionVolumeFactory'
import {
  createHangarWallMeshes,
  getAggregatedWallCollision,
  type WallCollisionResult,
} from './HangarWallCollisionBuilder'
import { createAllUnitWallMeshes, getAggregatedUnitWallCollision } from './UnitWallCollisionBuilder'
import {
  COLLISION_CHECK_RADIUS_FEET,
  COLLISION_CHECK_RADIUS_METERS,
  MIN_COLLISION_CHECK_INTERVAL,
  MAX_COLLISION_CHECKS_PER_SECOND,
  PERFORMANCE_REPORT_INTERVAL,
  ENABLE_COLLISION_DEBUG,
} from '@/constants/collision'
import type { CollisionResult, HangarCollisionConfig, UnitCollisionConfig, WallCollisionInfo } from '@/types/collision'
import type { PlacedAircraft3D } from '@/services/3d'

// Performance monitoring
let collisionCheckCount = 0
let lastPerformanceReport = 0
let lastCollisionCheck = 0

// Debug logging state
let collisionDebugEnabled = ENABLE_COLLISION_DEBUG

/**
 * Enable collision debug logging
 */
export function enableCollisionDebug(): void {
  collisionDebugEnabled = true
  console.log('✅ Collision debug logging enabled')
}

/**
 * Disable collision debug logging
 */
export function disableCollisionDebug(): void {
  collisionDebugEnabled = false
  console.log('❌ Collision debug logging disabled')
}

// Rate limiting for debug logs
const logRateLimit = new Map<string, number>()
const LOG_INTERVAL = 5000

function rateLimitedLog(key: string, message: string): void {
  const now = Date.now()
  const lastLog = logRateLimit.get(key) || 0

  if (now - lastLog > LOG_INTERVAL) {
    console.log(message)
    logRateLimit.set(key, now)
  }
}

/**
 * Get aircraft IDs within proximity radius of a specific aircraft
 * Uses REAL-TIME Three.js object positions (not stale placedPlanes array)
 */
function getNearbyAircraftIds(
  targetAircraftId: string,
  placedPlanes: PlacedAircraft3D[],
  sceneObjects: Map<string, THREE.Object3D>
): string[] {
  // Get CURRENT position from Three.js object (not stale placedPlanes)
  const targetObj = sceneObjects.get(targetAircraftId)
  if (!targetObj) return []

  const nearby: string[] = []
  // Three.js positions are already in meters
  const targetX = targetObj.position.x
  const targetZ = targetObj.position.z

  for (const plane of placedPlanes) {
    if (plane.id === targetAircraftId) continue // Skip self

    // Get CURRENT position from Three.js object
    const planeObj = sceneObjects.get(plane.id)
    if (!planeObj) continue

    const dx = planeObj.position.x - targetX
    const dz = planeObj.position.z - targetZ
    const distance = Math.sqrt(dx * dx + dz * dz)

    if (distance <= COLLISION_CHECK_RADIUS_METERS) {
      nearby.push(plane.id)
    }
  }

  return nearby
}

/**
 * Check precise collision using BVH triangle-level intersection
 */
function checkPreciseCollision(
  object1: THREE.Object3D,
  typeId1: string,
  object2: THREE.Object3D,
  typeId2: string
): boolean {
  // Ensure world matrices are up to date
  object1.updateMatrixWorld(true)
  object2.updateMatrixWorld(true)

  // Ensure BVH trees are computed for both objects
  ensureBVHForObject(object1, typeId1)
  ensureBVHForObject(object2, typeId2)

  // Collect meshes from both objects
  const meshes1: THREE.Mesh[] = []
  const meshes2: THREE.Mesh[] = []

  object1.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      if (child.geometry.boundsTree) {
        meshes1.push(child as THREE.Mesh)
      }
    }
  })

  object2.traverse((child) => {
    if (child instanceof THREE.Mesh) {
      if (child.geometry.boundsTree) {
        meshes2.push(child as THREE.Mesh)
      }
    }
  })

  // Check each mesh from object1 against each mesh from object2
  for (const mesh1 of meshes1) {
    for (const mesh2 of meshes2) {
      // Get the inverse of mesh2's world matrix
      const mesh2InverseMatrix = mesh2.matrixWorld.clone().invert()

      // Transform mesh1 into mesh2's local space
      const mesh1WorldMatrix = mesh1.matrixWorld.clone()
      const mesh1InMesh2Space = mesh1WorldMatrix.premultiply(mesh2InverseMatrix)

      // Get geometries
      const geometry1 = mesh1.geometry
      const geometry2 = mesh2.geometry

      if (geometry1.boundsTree && geometry2.boundsTree) {
        // Use intersectsGeometry for precise intersection test
        // @ts-expect-error - boundsTree is added by three-mesh-bvh
        const hit = geometry2.boundsTree.intersectsGeometry(geometry1, mesh1InMesh2Space)

        if (hit) {
          return true
        }
      }
    }
  }

  return false
}

/**
 * Main collision detection function
 * Supports multiple hangars and proximity-based optimization
 *
 * @param placedPlanes - Array of placed aircraft with position data
 * @param sceneObjects - Map of aircraft ID to Three.js Object3D
 * @param hangarConfigs - Array of hangar configurations for wall collision (legacy)
 * @param movingAircraftId - Optional: Only check this aircraft (optimization for drag)
 * @param unitConfigs - Optional: Array of unit configurations for edge-based wall collision
 * @returns CollisionResult with set of colliding aircraft IDs
 */
export function checkCollisions(
  placedPlanes: PlacedAircraft3D[],
  sceneObjects: Map<string, THREE.Object3D>,
  hangarConfigs: HangarCollisionConfig[],
  movingAircraftId?: string,
  unitConfigs?: UnitCollisionConfig[]
): CollisionResult {
  const startTime = performance.now()

  // Throttle collision checks to prevent excessive computation
  if (startTime - lastCollisionCheck < MIN_COLLISION_CHECK_INTERVAL) {
    if (collisionDebugEnabled) {
      console.warn(
        `⚠️ Collision check throttled - too frequent (${(startTime - lastCollisionCheck).toFixed(0)}ms since last)`
      )
    }
    return {
      collisions: new Set<string>(),
      isPartialCheck: false,
      checkedAircraftIds: [],
      checkDurationMs: 0,
    }
  }

  lastCollisionCheck = startTime
  collisionCheckCount++

  const collisions = new Set<string>()
  const aircraftOBBs = new Map<string, OBB[]>()
  const checkedAircraftIds: string[] = []
  const wallCollisions = new Map<string, WallCollisionInfo>()

  // Create collision volumes for each aircraft
  for (const plane of placedPlanes) {
    const sceneObject = sceneObjects.get(plane.id)
    if (sceneObject) {
      // Determine if we should use multi-component collision
      const useMultiComponent = shouldUseMultiComponent(sceneObject)

      let obbs: OBB[]
      if (useMultiComponent) {
        obbs = createMultiComponentOBBs(sceneObject)
      } else {
        obbs = [createConvexHullOBB(sceneObject)]
      }

      aircraftOBBs.set(plane.id, obbs)

      // Debug log collision method (rate limited)
      if (import.meta.env.DEV) {
        const analysisKey = `hull_analysis_${plane.typeId}`
        const method = useMultiComponent ? 'multi-component' : 'single convex hull'
        rateLimitedLog(
          analysisKey,
          `🚁 Collision: ${plane.typeId} using ${method} detection (${obbs.length} volume(s))`
        )
      }
    }
  }

  // Determine which aircraft pairs to check
  let aircraftPairsToCheck: Array<[string, string]> = []
  const isPartialCheck = !!(movingAircraftId && aircraftOBBs.has(movingAircraftId))

  if (isPartialCheck) {
    // Proximity-based: Only check moving aircraft against nearby aircraft
    const nearbyIds = getNearbyAircraftIds(movingAircraftId!, placedPlanes, sceneObjects)
    aircraftPairsToCheck = nearbyIds.map(
      (nearbyId) => [movingAircraftId!, nearbyId] as [string, string]
    )

    checkedAircraftIds.push(movingAircraftId!)
    checkedAircraftIds.push(...nearbyIds)

    if (collisionDebugEnabled && aircraftPairsToCheck.length > 0) {
      console.log(
        `🎯 Proximity check: ${movingAircraftId} against ${nearbyIds.length} nearby aircraft (within ${COLLISION_CHECK_RADIUS_FEET}ft)`
      )
    }
  } else {
    // Full check: Check all pairs
    const aircraftIds = Array.from(aircraftOBBs.keys())
    checkedAircraftIds.push(...aircraftIds)

    for (let i = 0; i < aircraftIds.length; i++) {
      for (let j = i + 1; j < aircraftIds.length; j++) {
        aircraftPairsToCheck.push([aircraftIds[i], aircraftIds[j]])
      }
    }
  }

  // STAGE 1 & 2: Aircraft-to-aircraft collision checks
  for (const [id1, id2] of aircraftPairsToCheck) {
    const obbs1 = aircraftOBBs.get(id1)
    const obbs2 = aircraftOBBs.get(id2)

    if (!obbs1 || !obbs2) continue

    // STAGE 1: Fast OBB broad-phase check
    let hasOBBCollision = false
    for (const obb1 of obbs1) {
      for (const obb2 of obbs2) {
        if (obb1.intersectsOBB(obb2)) {
          hasOBBCollision = true
          break
        }
      }
      if (hasOBBCollision) break
    }

    // STAGE 2: Precise BVH mesh-level check (only if OBB detected collision)
    if (hasOBBCollision) {
      const plane1 = placedPlanes.find((p) => p.id === id1)!
      const plane2 = placedPlanes.find((p) => p.id === id2)!
      const obj1 = sceneObjects.get(id1)!
      const obj2 = sceneObjects.get(id2)!

      const hasPreciseCollision = checkPreciseCollision(
        obj1,
        plane1.typeId,
        obj2,
        plane2.typeId
      )

      if (hasPreciseCollision) {
        collisions.add(id1)
        collisions.add(id2)

        if (collisionDebugEnabled) {
          console.log(`✅ Two-stage collision confirmed: ${id1} <-> ${id2}`)
        }
      }
    }
  }

  // STAGE 1 & 2: Aircraft-to-wall collision checks
  if (hangarConfigs.length > 0) {
    // Create wall collision meshes for all hangars
    const hangarWalls = new Map<string, WallCollisionResult>()
    for (const config of hangarConfigs) {
      hangarWalls.set(config.areaId, createHangarWallMeshes(config))
    }

    const { allOBBs, allMeshes, wallToHangarMap } = getAggregatedWallCollision(hangarWalls)

    // Determine which aircraft to check against walls
    const aircraftToCheckForWalls =
      movingAircraftId && aircraftOBBs.has(movingAircraftId)
        ? [[movingAircraftId, aircraftOBBs.get(movingAircraftId)!] as [string, OBB[]]]
        : Array.from(aircraftOBBs.entries())

    for (const [aircraftId, aircraftOBBList] of aircraftToCheckForWalls) {
      // STAGE 1: Collect ALL OBB broad-phase candidates (don't break on first)
      const candidateWallIndices: number[] = []

      for (let w = 0; w < allOBBs.length; w++) {
        for (const aircraftOBB of aircraftOBBList) {
          if (aircraftOBB.intersectsOBB(allOBBs[w])) {
            candidateWallIndices.push(w)
            break // break inner loop only — this wall is a candidate
          }
        }
      }

      if (candidateWallIndices.length === 0) continue

      if (collisionDebugEnabled) {
        console.log(
          `🧱 OBB wall collision: ${aircraftId} has ${candidateWallIndices.length} candidate wall(s) - checking BVH narrow phase`
        )
      }

      // Prepare aircraft meshes once (reuse across all candidate walls)
      const aircraftObject = sceneObjects.get(aircraftId)!
      aircraftObject.updateMatrixWorld(true)

      const aircraft = placedPlanes.find((p) => p.id === aircraftId)!
      ensureBVHForObject(aircraftObject, aircraft.typeId)

      const aircraftMeshes: THREE.Mesh[] = []
      aircraftObject.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry.boundsTree) {
            aircraftMeshes.push(child as THREE.Mesh)
          }
        }
      })

      // STAGE 2: BVH narrow-phase check each candidate until confirmed
      let confirmed = false
      for (const wallIdx of candidateWallIndices) {
        const wallMesh = allMeshes[wallIdx]
        wallMesh.updateMatrixWorld(true)

        for (const aircraftMesh of aircraftMeshes) {
          aircraftMesh.updateMatrixWorld(true)

          const wallInverseMatrix = wallMesh.matrixWorld.clone().invert()
          const aircraftInWallSpace = aircraftMesh.matrixWorld.clone().premultiply(wallInverseMatrix)

          const aircraftGeometry = aircraftMesh.geometry
          const wallGeometry = wallMesh.geometry

          if (aircraftGeometry.boundsTree && wallGeometry.boundsTree) {
            // @ts-expect-error - boundsTree is added by three-mesh-bvh
            const hit = wallGeometry.boundsTree.intersectsGeometry(
              aircraftGeometry,
              aircraftInWallSpace
            )

            if (hit) {
              confirmed = true
              collisions.add(aircraftId)

              if (collisionDebugEnabled) {
                const wallInfo = wallToHangarMap.get(wallIdx)
                console.log(
                  `✅ BVH wall collision confirmed: ${aircraftId} (wall ${wallIdx}, hangar ${wallInfo?.areaId})`
                )
              }
              break
            }
          }
        }
        if (confirmed) break
      }

      if (!confirmed && collisionDebugEnabled) {
        console.log(
          `❌ BVH wall collision rejected (all ${candidateWallIndices.length} OBB candidates were false positives): ${aircraftId}`
        )
      }
    }
  }

  // STAGE 1 & 2: Aircraft-to-unit-wall collision checks (new edge-based system)
  if (unitConfigs && unitConfigs.length > 0) {
    // Create wall collision meshes for all units
    const unitWalls = createAllUnitWallMeshes(unitConfigs)
    const { allOBBs: unitOBBs, allMeshes: unitMeshes, wallToUnitMap } =
      getAggregatedUnitWallCollision(unitWalls)

    if (collisionDebugEnabled) {
      console.log(`🏗️ Unit wall collision: ${unitConfigs.length} units, ${unitOBBs.length} wall segments`)
    }

    // Determine which aircraft to check against unit walls
    const aircraftToCheckForWalls =
      movingAircraftId && aircraftOBBs.has(movingAircraftId)
        ? [[movingAircraftId, aircraftOBBs.get(movingAircraftId)!] as [string, OBB[]]]
        : Array.from(aircraftOBBs.entries())

    for (const [aircraftId, aircraftOBBList] of aircraftToCheckForWalls) {
      // Skip if already marked as colliding (from aircraft-to-aircraft or hangar walls)
      if (collisions.has(aircraftId)) continue

      // STAGE 1: Collect ALL OBB broad-phase candidates (don't break on first)
      const candidateUnitWallIndices: number[] = []

      for (let w = 0; w < unitOBBs.length; w++) {
        for (const aircraftOBB of aircraftOBBList) {
          if (aircraftOBB.intersectsOBB(unitOBBs[w])) {
            candidateUnitWallIndices.push(w)
            break // break inner loop only — this wall is a candidate
          }
        }
      }

      if (candidateUnitWallIndices.length === 0) continue

      if (collisionDebugEnabled) {
        console.log(
          `🏗️ OBB unit wall collision: ${aircraftId} has ${candidateUnitWallIndices.length} candidate wall(s) - checking BVH narrow phase`
        )
      }

      // Prepare aircraft meshes once (reuse across all candidate walls)
      const aircraftObject = sceneObjects.get(aircraftId)!
      aircraftObject.updateMatrixWorld(true)

      const aircraft = placedPlanes.find((p) => p.id === aircraftId)!
      ensureBVHForObject(aircraftObject, aircraft.typeId)

      const aircraftMeshes: THREE.Mesh[] = []
      aircraftObject.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          if (child.geometry.boundsTree) {
            aircraftMeshes.push(child as THREE.Mesh)
          }
        }
      })

      // STAGE 2: BVH narrow-phase check each candidate until confirmed
      let confirmed = false
      let confirmedWallIdx = -1
      for (const wallIdx of candidateUnitWallIndices) {
        const unitWallMesh = unitMeshes[wallIdx]
        unitWallMesh.updateMatrixWorld(true)

        for (const aircraftMesh of aircraftMeshes) {
          aircraftMesh.updateMatrixWorld(true)

          const wallInverseMatrix = unitWallMesh.matrixWorld.clone().invert()
          const aircraftInWallSpace = aircraftMesh.matrixWorld.clone().premultiply(wallInverseMatrix)

          const aircraftGeometry = aircraftMesh.geometry
          const wallGeometry = unitWallMesh.geometry

          if (aircraftGeometry.boundsTree && wallGeometry.boundsTree) {
            // @ts-expect-error - boundsTree is added by three-mesh-bvh
            const hit = wallGeometry.boundsTree.intersectsGeometry(
              aircraftGeometry,
              aircraftInWallSpace
            )

            if (hit) {
              confirmed = true
              confirmedWallIdx = wallIdx
              break
            }
          }
        }
        if (confirmed) break
      }

      if (confirmed) {
        collisions.add(aircraftId)

        const wallInfo = wallToUnitMap.get(confirmedWallIdx)
        if (wallInfo) {
          wallCollisions.set(aircraftId, {
            side: wallInfo.side,
            unitId: wallInfo.unitId,
            areaId: wallInfo.areaId,
            isDoor: wallInfo.isDoor,
          })
        }

        if (collisionDebugEnabled) {
          const typeLabel = wallInfo?.isDoor ? 'door' : 'wall'
          console.log(
            `✅ BVH unit ${typeLabel} collision confirmed: ${aircraftId} (unit ${wallInfo?.unitId}, side ${wallInfo?.side})`
          )
        }
      } else if (collisionDebugEnabled) {
        console.log(
          `❌ BVH unit wall collision rejected (all ${candidateUnitWallIndices.length} OBB candidates were false positives): ${aircraftId}`
        )
      }
    }
  }

  // Performance monitoring
  const endTime = performance.now()
  const duration = endTime - startTime

  if (import.meta.env.DEV) {
    // Warn if collision check takes too long
    if (duration > 50) {
      console.warn(
        `⚠️ Slow collision check: ${duration.toFixed(2)}ms for ${placedPlanes.length} aircraft`
      )
    }

    // Periodic performance report
    if (endTime - lastPerformanceReport > PERFORMANCE_REPORT_INTERVAL) {
      console.log(
        `📈 Collision Performance: ${collisionCheckCount} checks, ~${duration.toFixed(2)}ms avg`
      )
      lastPerformanceReport = endTime

      // Warn if collision checks are happening too frequently
      const checksPerSecond = collisionCheckCount / (PERFORMANCE_REPORT_INTERVAL / 1000)
      if (checksPerSecond > MAX_COLLISION_CHECKS_PER_SECOND) {
        console.warn(
          `🚨 EXCESSIVE collision check frequency: ${checksPerSecond.toFixed(1)} checks/sec (max: ${MAX_COLLISION_CHECKS_PER_SECOND})`
        )
      }

      collisionCheckCount = 0
    }
  }

  return {
    collisions,
    isPartialCheck,
    checkedAircraftIds,
    checkDurationMs: duration,
    wallCollisions,
  }
}

/**
 * Simple debounce function for throttling collision checks during drag
 */
export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  delay: number
): (...args: Parameters<T>) => void {
  let timeoutId: ReturnType<typeof setTimeout>
  return (...args: Parameters<T>) => {
    clearTimeout(timeoutId)
    timeoutId = setTimeout(() => func(...args), delay)
  }
}

/**
 * Create a debounced version of checkCollisions for use during drag
 */
export const debouncedCheckCollisions = debounce(checkCollisions, 100)
